import { createQuery } from '@tanstack/svelte-query';
import { fromStore, get, toStore, type Writable } from 'svelte/store';
import {
	GetEditionsCartDocument,
	type GetEditionsCartQuery,
} from '../../queries/__generated__/getEditionsCart.generated';
import { getEditionsCartQueryVariables } from '../getEditionsCartQueryVariables/getEditionsCartQueryVariables';
import { FeatureFlags } from '#lib/constants/feature-flags.js';
import { NONEXISTENT_PRODUCT_ID } from '#lib/features/checkout/constants/nonexistent-product-id.js';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { getEditionsCart } from '#lib/websites/editions/api/getEditionsCart.js';
import { stringifyIds } from '#lib/websites/nft/utils/stringifyIds/stringifyIds.js';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import { getImageUrl } from '$global/utils/getImageUrl';

export const createEditionsQuery = (
	items: Writable<{ productId: string; quantity: number }[]>,
	defaultLoading?: boolean
) => {
	const items$ = fromStore(items);

	const editionsQuery = createQuery(() => {
		const $items = items$.current;
		const ids = $items?.map(({ productId }) => productId);
		const queryIds = ids?.length ? ids : [NONEXISTENT_PRODUCT_ID];
		const variables = getEditionsCartQueryVariables(queryIds);

		return FeatureFlags.RestPages['Editions cart hydration']
			? {
					queryKey: getQueryKeys(GetEditionsCartDocument, variables),
					queryFn: async (): Promise<GetEditionsCartQuery> =>
						stringifyIds(await getEditionsCart(fetch, queryIds)),
				}
			: getQuery(GetEditionsCartDocument, variables);
	});

	return toStore(() => ({
		...editionsQuery,
		error: editionsQuery.error || null,
		data: editionsQuery.data || { Editions: [] },
		isLoading: (() => {
			if (!editionsQuery.data) {
				return typeof defaultLoading !== 'undefined' ? defaultLoading : false;
			}

			return editionsQuery.isPending;
		})(),
		formattedItems: (editionsQuery.data?.Editions || []).map((edition) => {
			const quantity = get(items).find(
				({ productId }) => edition?.product?.id === productId
			)?.quantity;

			const allDefinedImage = edition.image ? edition.image : null;

			return {
				dropConfigId: editionsQuery.data?.Drop_Config?.[0]?.id,
				image: allDefinedImage
					? {
							url: `${getImageUrl(allDefinedImage?.filename_disk, '512px')}`,
							width: allDefinedImage.width || 0,
							height: allDefinedImage.height || 0,
						}
					: null,
				line1: edition?.artists
					?.map((artist) => artist?.Artists_id?.name)
					?.join(', '),
				line2: `${edition?.code} | ${edition?.name}`,
				line3:
					edition.dimensions_description ||
					`${edition?.length_cm} x ${edition?.width_cm} CM`,
				productId: edition?.product?.id,
				id: edition?.id,
				quantity: quantity,
				currency: edition?.product?.currency,
				sku: edition?.product?.sku,
				price: edition?.product?.price,
				variant: null,
				// Some editions don't have a series
				groupId: edition?.series?.code || edition?.code,
				groupName: edition?.series?.name || edition?.name,
			};
		}),
	}));
};
